import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, fromEvent, Subject, Subscription } from 'rxjs';
import { auditTime, concatMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MiaV3Service } from '../services/mia-v3.service';
import { SocketService } from '../services/socket.service';
import { AutorizacaoService } from '../services/autorizacao.service';
import { custoEmReaisFormatado as formateCustoEmReais } from './custo-ia';

interface ItemChat {
  tipo: 'separador' | 'turno';
  label?: string;
  turno?: any;
}

const CORES_AVATAR = ['#00a884', '#7c3aed', '#0ea5e9', '#f59e0b', '#ef4444', '#ec4899', '#10b981', '#6366f1'];

// listas longas colapsam: mostra 6 e o botão "Ver todas"; até 8 cabem sem colapsar
const OPCOES_VISIVEIS_COLAPSADO = 6;
const MAXIMO_DE_OPCOES_SEM_COLAPSAR = 8;

// acima disso a latência aparece no badge (âmbar) — abaixo, só no detalhe expandido
const LATENCIA_LENTA_MS = 5000;

@Component({
  selector: 'app-conversas-mia-v3',
  templateUrl: './conversas-mia-v3.component.html',
  styleUrls: ['./conversas-mia-v3.component.scss']
})
export class ConversasMiaV3Component implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('corpoChat') corpoChat: ElementRef;
  @ViewChild('campoResposta') campoResposta: ElementRef;

  conversas: any[] = [];
  conversaSelecionada: any = null;
  itensChat: ItemChat[] = [];
  // agregado dos turnos da conversa aberta (modelos usados + tokens somados)
  resumoTelemetria: { modelos: string[]; entrada: number; saida: number; cache: number; cacheEscrita: number } | null = null;

  carregandoLista = true;
  carregandoChat = false;
  busca = '';
  aoVivo = false;
  filtro: 'todas' | 'atendente' | 'nao_converteu' | 'venderam' = 'todas';

  // Período da lista (última atividade da conversa). A fila de atendimento
  // ignora o período: fila é fila, gente esperando não pode sumir por data.
  periodoDias = 7;
  periodoPersonalizado = false;
  periodoDe = '';
  periodoAte = '';
  resposta = '';
  alterandoEstado = false;
  enviandoResposta = false;
  erroAcao = '';
  ultimaMensagemClienteEm: Date | null = null;
  agora = Date.now();
  respostasRapidas = [
    { rotulo: 'Já verifico', texto: 'Um momento, por favor. Já vou verificar isso para você.' },
    { rotulo: 'Obrigado por aguardar', texto: 'Obrigado por aguardar! Já estou com seu atendimento.' },
    { rotulo: 'Pode me confirmar?', texto: 'Pode me confirmar essa informação, por favor?' },
    { rotulo: 'Mais alguma coisa?', texto: 'Posso ajudar com mais alguma coisa?' },
  ];

  private busca$ = new Subject<string>();
  private atualizacoes$ = new Subject<void>();
  private assinaturas: Subscription[] = [];
  private leiturasEmAndamento = new Set<number>();
  private deveRolarParaOFim = false;
  private rascunhos: { [conversaId: string]: string } = {};
  private usuario: any = {};

  constructor(private miaV3Service: MiaV3Service, private socketService: SocketService,
              private autorizacaoService: AutorizacaoService,
              private router: Router, private rota: ActivatedRoute) {}

  /** Telemetria de IA (modelo, tokens, custo, cache) é assunto interno do MeuCardápio. */
  get ehAdmin(): boolean {
    return !!(this.usuario.admin || this.usuario.superadmin);
  }

  /** O painel linka para cá com o filtro e o período já escolhidos. */
  private apliqueParametrosDaUrl(): void {
    const params = this.rota.snapshot.queryParamMap;

    const filtro = params.get('filtro');
    if (filtro === 'nao_converteu' || filtro === 'venderam' || filtro === 'atendente') {
      this.filtro = filtro;
    }

    const de = params.get('de');
    const ate = params.get('ate');
    if (de && ate) {
      this.periodoPersonalizado = true;
      this.periodoDe = de;
      this.periodoAte = ate;
      return;
    }

    const dias = Number(params.get('dias'));
    if ([1, 7, 30].includes(dias)) this.periodoDias = dias;
  }

  /** Abre o simulador (Testar Mia V3), que já começa uma conversa nova. */
  novaConversa() {
    this.router.navigateByUrl('/admin/mia-v3/testar');
  }

  ngOnInit(): void {
    this.usuario = this.autorizacaoService.getUser() || {};
    this.apliqueParametrosDaUrl();
    this.carregueConversas();

    this.assinaturas.push(
      this.busca$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.carregueConversas())
    );

    // Agrupa rajadas de mensagens/status e serializa as consultas para não aplicar respostas fora de ordem.
    this.assinaturas.push(
      this.atualizacoes$.pipe(
        auditTime(200),
        concatMap(() => from(this.sincronizeSilenciosamente()))
      ).subscribe()
    );

    this.assinaturas.push(
      this.socketService.on('mia3:atualizacao').subscribe(() => this.soliciteAtualizacao())
    );
    // Compatibilidade com processos que ainda emitam somente o evento antigo de escalação.
    this.assinaturas.push(
      this.socketService.on('mia3:escalacao').subscribe(() => this.soliciteAtualizacao())
    );

    this.aoVivo = this.socketService.conectado;
    this.assinaturas.push(this.socketService.on('connect').subscribe(() => {
      this.aoVivo = true;
      this.soliciteAtualizacao();
    }));
    this.assinaturas.push(this.socketService.on('disconnect').subscribe(() => this.aoVivo = false));
    this.assinaturas.push(fromEvent(document, 'visibilitychange').subscribe(() => {
      if (!document.hidden) {
        this.agora = Date.now();
        this.soliciteAtualizacao();
      }
    }));
  }

  ngOnDestroy(): void {
    this.assinaturas.forEach(assinatura => assinatura.unsubscribe());
  }

  ngAfterViewChecked(): void {
    if (this.deveRolarParaOFim && this.corpoChat) {
      this.corpoChat.nativeElement.scrollTop = this.corpoChat.nativeElement.scrollHeight;
      this.deveRolarParaOFim = false;
    }
  }

  aoDigitarBusca() {
    this.busca$.next(this.busca);
  }

  carregueConversas(silencioso = false): Promise<void> {
    if (!silencioso) this.carregandoLista = true;

    return this.miaV3Service.obtenhaConversas(this.busca, this.periodoAtual()).then(conversas => {
      this.conversas = conversas;
      if (this.conversaSelecionada) {
        const selecionadaId = Number(this.conversaSelecionada.id);
        const atualizada = conversas.find(conversa => Number(conversa.id) === selecionadaId);
        if (atualizada) this.conversaSelecionada = { ...this.conversaSelecionada, ...atualizada };
      }
      this.carregandoLista = false;
    }).catch(() => {
      this.carregandoLista = false;
    });
  }

  get conversasVisiveis(): any[] {
    if (this.filtro === 'atendente') {
      return this.conversas.filter(conversa => conversa.estado === 'PAUSADA_ATENDENTE');
    }
    if (this.filtro === 'nao_converteu') {
      return this.conversas.filter(conversa => this.naoConverteu(conversa));
    }
    if (this.filtro === 'venderam') {
      return this.conversas.filter(conversa => Number(conversa.pedidosGerados) > 0);
    }
    return this.conversas;
  }

  get quantidadeComAtendente(): number {
    return this.conversas.filter(conversa => conversa.estado === 'PAUSADA_ATENDENTE').length;
  }

  get quantidadeNaoConverteram(): number {
    return this.conversas.filter(conversa => this.naoConverteu(conversa)).length;
  }

  /**
   * Perdeu venda DENTRO do período consultado — a mesma conta do painel.
   * O último desfecho de todos os tempos fazia perda antiga de conversa
   * ativa recentemente aparecer na lista de 7 dias.
   */
  private naoConverteu(conversa: any): boolean {
    return Number(conversa.perdasNoPeriodo) > 0;
  }

  /**
   * Rótulo humano do motivo (mesmos textos do painel) para a etiqueta da linha.
   * O motivo gravado é o do ÚLTIMO episódio: se depois da perda veio outro
   * atendimento (um pós-venda, por exemplo), a etiqueta fica de fora em vez
   * de rotular a perda com o motivo do episódio errado.
   */
  etiquetaMotivo(conversa: any): string | null {
    if (!this.naoConverteu(conversa) || Number(conversa.pedidosGerados) > 0) return null;
    if (conversa.ultimoDesfecho !== 'SEM_PEDIDO') return null;
    const rotulos: { [motivo: string]: string } = {
      ABANDONO_CARRINHO: 'Montou o carrinho e sumiu',
      PAGAMENTO_FALHOU: 'O pagamento não passou',
      FORA_AREA: 'Fora da área de entrega',
      LOJA_FECHADA: 'A loja estava fechada',
      ITEM_INDISPONIVEL: 'Procurou algo que não tem',
      DESISTENCIA: 'Desistiu durante a conversa'
    };
    return rotulos[conversa.ultimoMotivo] || null;
  }

  /** null quando a fila de atendimento está ativa — ela não filtra por data. */
  private periodoAtual(): { de?: string, ate?: string } | undefined {
    if (this.filtro === 'atendente') return undefined;

    if (this.periodoPersonalizado) {
      if (!this.periodoValido) return undefined;
      return { de: `${this.periodoDe} 00:00:00`, ate: `${this.periodoAte} 23:59:59` };
    }

    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    inicio.setDate(inicio.getDate() - (this.periodoDias - 1));
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    const dia = `${inicio.getFullYear()}-${doisDigitos(inicio.getMonth() + 1)}-${doisDigitos(inicio.getDate())}`;
    return { de: `${dia} 00:00:00` };
  }

  get periodoValido(): boolean {
    return !!this.periodoDe && !!this.periodoAte && this.periodoDe <= this.periodoAte;
  }

  selecionePeriodo(dias: number): void {
    if (dias === this.periodoDias && !this.periodoPersonalizado) return;
    this.periodoPersonalizado = false;
    this.periodoDias = dias;
    this.carregueConversas();
  }

  ativePeriodoPersonalizado(): void {
    if (this.periodoPersonalizado) return;
    this.periodoPersonalizado = true;
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    const formate = (data: Date) =>
      `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - (this.periodoDias - 1));
    this.periodoDe = formate(inicio);
    this.periodoAte = formate(new Date());
  }

  apliquePeriodo(): void {
    if (!this.periodoValido) return;
    this.carregueConversas();
  }

  rastreieConversa(indice: number, conversa: any): string {
    return `conversa:${conversa?.id ?? indice}`;
  }

  rastreieItemChat(indice: number, item: ItemChat): string {
    if (item.tipo === 'turno') return `turno:${item.turno?.id ?? indice}`;
    return `separador:${item.label ?? indice}`;
  }

  altereFiltro(filtro: 'todas' | 'atendente' | 'nao_converteu' | 'venderam') {
    if (this.filtro === filtro) return;
    const mudaConsulta = filtro === 'atendente' || this.filtro === 'atendente';
    this.filtro = filtro;
    // entrar/sair da fila muda o universo consultado (a fila ignora o período)
    if (mudaConsulta) this.carregueConversas();
  }

  abraConversa(conversa: any) {
    if (this.conversaSelecionada?.id === conversa.id) return;
    this.salveRascunho();
    this.conversaSelecionada = conversa;
    this.resposta = this.rascunhos[String(conversa.id)] || '';
    this.itensChat = [];
    this.ultimaMensagemClienteEm = null;
    this.erroAcao = '';
    this.carregueTurnos(conversa);
  }

  fecheConversa() {
    this.salveRascunho();
    this.conversaSelecionada = null;
    this.itensChat = [];
    this.ultimaMensagemClienteEm = null;
    this.erroAcao = '';
  }

  private carregueTurnos(conversa: any, silencioso = false): Promise<void> {
    const acompanhandoOFim = !silencioso || this.estaNoFimDoChat();
    if (!silencioso) {
      this.carregandoChat = true;
      this.resumoTelemetria = null; // não mostrar o resumo da conversa anterior
    }

    return this.miaV3Service.obtenhaTurnos(conversa.id).then(({ conversa: conversaAtualizada, turnos }) => {
      if (!this.conversaSelecionada || Number(this.conversaSelecionada.id) !== Number(conversa.id)) return;
      if (conversaAtualizada) this.atualizeConversa(conversaAtualizada);
      this.itensChat = this.monteItensChat(turnos);
      this.resumoTelemetria = this.calculeResumoTelemetria(turnos);
      const ultimoTurnoCliente = [...turnos].reverse().find(turno => turno.papel === 'cliente');
      this.agora = Date.now();
      this.ultimaMensagemClienteEm = ultimoTurnoCliente?.criadoEm
        ? new Date(ultimoTurnoCliente.criadoEm)
        : null;
      this.carregandoChat = false;
      this.deveRolarParaOFim = acompanhandoOFim;
      this.marqueMensagensVisiveisComoLidas(conversa, turnos);
      if (!silencioso && this.conversaSelecionada.estado === 'PAUSADA_ATENDENTE') {
        setTimeout(() => this.foqueCampoResposta());
      }
    }).catch(() => {
      this.carregandoChat = false;
    });
  }

  private soliciteAtualizacao() {
    this.atualizacoes$.next();
  }

  private sincronizeSilenciosamente(): Promise<void> {
    const selecionada = this.conversaSelecionada;
    const consultas: Array<Promise<void>> = [this.carregueConversas(true)];
    if (selecionada && !this.enviandoResposta) consultas.push(this.carregueTurnos(selecionada, true));
    return Promise.all(consultas).then(() => undefined);
  }

  private estaNoFimDoChat(): boolean {
    if (!this.corpoChat) return true;
    const elemento = this.corpoChat.nativeElement;
    return elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight < 80;
  }

  private marqueMensagensVisiveisComoLidas(conversa: any, turnos: any[]) {
    if (document.hidden) return;

    const conversaId = Number(conversa?.id);
    const temMensagemPendente = turnos.some(turno =>
      turno?.papel === 'cliente' && turno?.wamid && turno?.statusWhatsapp !== 'read'
    );
    if (!conversaId || !temMensagemPendente || this.leiturasEmAndamento.has(conversaId)) return;

    this.leiturasEmAndamento.add(conversaId);
    this.miaV3Service.marqueConversaComoLida(conversaId)
      .catch(() => undefined)
      .finally(() => this.leiturasEmAndamento.delete(conversaId));
  }

  assumaConversa() {
    if (!this.conversaSelecionada || this.alterandoEstado) return;
    this.alterandoEstado = true;
    this.erroAcao = '';

    this.miaV3Service.assumaConversa(this.conversaSelecionada.id).then((resposta: any) => {
      this.atualizeConversa(resposta?.conversa || { estado: 'PAUSADA_ATENDENTE' });
      this.alterandoEstado = false;
      setTimeout(() => this.foqueCampoResposta());
    }).catch(erro => {
      this.alterandoEstado = false;
      this.erroAcao = this.mensagemDoErro(erro, 'Não foi possível assumir a conversa.');
    });
  }

  devolvaConversa() {
    if (!this.conversaSelecionada || this.alterandoEstado || this.enviandoResposta) return;
    this.alterandoEstado = true;
    this.erroAcao = '';

    this.miaV3Service.devolvaConversa(this.conversaSelecionada.id).then((resposta: any) => {
      this.atualizeConversa(resposta?.conversa || { estado: 'ATIVA' });
      this.alterandoEstado = false;
    }).catch(erro => {
      this.alterandoEstado = false;
      this.erroAcao = this.mensagemDoErro(erro, 'Não foi possível devolver a conversa para a Mia.');
    });
  }

  envieResposta(textoForcado?: string) {
    if (!this.conversaSelecionada || this.enviandoResposta || !this.dentroDaJanela24h()) return;

    const texto = String(textoForcado != null ? textoForcado : this.resposta).trim();
    if (!texto) {
      this.foqueCampoResposta();
      return;
    }

    const conversaId = this.conversaSelecionada.id;
    const turnoOtimista = {
      id: `local-${Date.now()}`,
      conversaId,
      papel: 'atendente',
      texto,
      criadoEm: new Date(),
      statusEnvio: 'enviando',
      erroEnvio: '',
    };

    this.adicioneTurnoOtimista(turnoOtimista);
    this.resposta = '';
    this.rascunhos[String(conversaId)] = '';
    this.enviandoResposta = true;
    this.erroAcao = '';
    this.redefinaAlturaCampo();

    this.miaV3Service.respondaConversa(conversaId, texto).then((resposta: any) => {
      turnoOtimista.statusEnvio = resposta?.confirmacaoPendente ? 'aviso' : 'enviado';
      this.enviandoResposta = false;
      if (resposta?.confirmacaoPendente) {
        this.erroAcao = resposta.aviso;
        return;
      }
      if (this.conversaSelecionada?.id === conversaId) {
        this.carregueTurnos(this.conversaSelecionada, true);
        setTimeout(() => this.foqueCampoResposta());
      }
    }).catch(erro => {
      turnoOtimista.statusEnvio = 'erro';
      turnoOtimista.erroEnvio = this.mensagemDoErro(erro, 'Falha ao enviar. Tente novamente.');
      this.enviandoResposta = false;
      if (this.conversaSelecionada?.id === conversaId) {
        this.erroAcao = turnoOtimista.erroEnvio;
        this.deveRolarParaOFim = true;
      } else {
        this.rascunhos[String(conversaId)] = texto;
      }
    });
  }

  tenteNovamente(turno: any) {
    if (!turno || this.enviandoResposta) return;
    this.itensChat = this.itensChat.filter(item => item.turno !== turno);
    this.envieResposta(turno.texto);
  }

  aoPressionarResposta(evento: KeyboardEvent) {
    if (evento.key === 'Enter' && !evento.shiftKey && !(evento as any).isComposing) {
      evento.preventDefault();
      this.envieResposta();
    }
  }

  aoDigitarResposta(evento: any) {
    const campo = evento?.target;
    if (!campo) return;
    campo.style.height = 'auto';
    campo.style.height = `${Math.min(campo.scrollHeight, 132)}px`;
  }

  useRespostaRapida(texto: string) {
    const atual = this.resposta.trim();
    this.resposta = atual ? `${atual}\n${texto}` : texto;
    this.salveRascunho();
    setTimeout(() => {
      this.foqueCampoResposta();
      if (this.campoResposta) this.aoDigitarResposta({ target: this.campoResposta.nativeElement });
    });
  }

  dentroDaJanela24h(): boolean {
    if (!this.ultimaMensagemClienteEm) return false;
    return this.agora - this.ultimaMensagemClienteEm.getTime() < 24 * 60 * 60 * 1000;
  }

  textoDaJanela(): string {
    if (!this.ultimaMensagemClienteEm) return 'Aguardando uma mensagem do cliente';
    const restanteMs = (24 * 60 * 60 * 1000) - (this.agora - this.ultimaMensagemClienteEm.getTime());
    if (restanteMs <= 0) return 'Fora da janela de 24h';
    const horas = Math.floor(restanteMs / (60 * 60 * 1000));
    const minutos = Math.floor((restanteMs % (60 * 60 * 1000)) / (60 * 1000));
    return horas > 0 ? `Janela aberta por mais ${horas}h ${minutos}min` : `Janela aberta por mais ${minutos}min`;
  }

  private adicioneTurnoOtimista(turno: any) {
    const label = this.labelDoDia(turno.criadoEm);
    const ultimoSeparador = [...this.itensChat].reverse().find(item => item.tipo === 'separador');
    if (!ultimoSeparador || ultimoSeparador.label !== label) {
      this.itensChat.push({ tipo: 'separador', label });
    }
    this.prepareOpcoesDaLista(turno);
    this.prepareMidia(turno);
    this.itensChat.push({ tipo: 'turno', turno });
    this.deveRolarParaOFim = true;
  }

  private atualizeConversa(dados: any) {
    this.conversaSelecionada = { ...this.conversaSelecionada, ...dados };
    const indice = this.conversas.findIndex(conversa => Number(conversa.id) === Number(this.conversaSelecionada.id));
    if (indice >= 0) this.conversas[indice] = { ...this.conversas[indice], ...dados };
  }

  private salveRascunho() {
    if (this.conversaSelecionada) {
      this.rascunhos[String(this.conversaSelecionada.id)] = this.resposta;
    }
  }

  private foqueCampoResposta() {
    if (this.campoResposta && !this.campoResposta.nativeElement.disabled) {
      this.campoResposta.nativeElement.focus();
    }
  }

  private redefinaAlturaCampo() {
    setTimeout(() => {
      if (this.campoResposta) this.campoResposta.nativeElement.style.height = 'auto';
    });
  }

  private mensagemDoErro(erro: any, fallback: string): string {
    return erro?.error?.erro || erro?.message || fallback;
  }

  private monteItensChat(turnos: any[]): ItemChat[] {
    const itens: ItemChat[] = [];
    let diaAnterior = '';

    for (const turno of turnos) {
      const dia = this.labelDoDia(turno.criadoEm);
      if (dia !== diaAnterior) {
        itens.push({ tipo: 'separador', label: dia });
        diaAnterior = dia;
      }
      this.prepareOpcoesDaLista(turno);
      this.prepareMidia(turno);
      itens.push({ tipo: 'turno', turno });
    }

    return itens;
  }

  /**
   * JSON da coluna `midia` do turno ([{tipo, arquivo, nome, mime, legenda}])
   * vira as listas que o template renderiza: imagem aparece na bolha (clique
   * abre em tamanho real), áudio vira player e documento vira cartão com botão
   * de baixar. O arquivo é servido pela rota autenticada /miav3/midia/:arquivo.
   */
  private prepareMidia(turno: any) {
    turno.imagensMidia = [];
    turno.audiosMidia = [];
    turno.documentosMidia = [];
    if (!turno.midia) return;

    let midias: any[];
    try {
      midias = JSON.parse(turno.midia);
    } catch {
      return;
    }
    if (!Array.isArray(midias)) return;

    for (const midia of midias) {
      if (!midia?.arquivo) continue;
      const url = `/miav3/midia/${midia.arquivo}`;
      if (midia.tipo === 'imagem') {
        turno.imagensMidia.push({ url, legenda: midia.legenda || '' });
      } else if (midia.tipo === 'audio') {
        turno.audiosMidia.push({ url, mime: midia.mime || '' });
      } else {
        const nome = midia.nome || 'documento';
        const extensao = String(midia.arquivo.split('.').pop() || '').toUpperCase();
        turno.documentosMidia.push({
          nome,
          extensao,
          urlDownload: `${url}?baixar=1&nome=${encodeURIComponent(nome)}`,
          icone: extensao === 'PDF' ? 'fa-file-pdf' : 'fa-file-alt',
        });
      }
    }
  }

  /**
   * A transcrição grava mensagem interativa de lista como corpo + linhas "• opção"
   * no final. Separa esse bloco para o template renderizar como cartão de opções
   * em vez de textão corrido.
   */
  private prepareOpcoesDaLista(turno: any) {
    turno.corpoExibicao = turno.texto;
    turno.opcoesLista = [];
    if (turno.papel !== 'mia') return;

    const linhas = String(turno.texto || '').split('\n');
    let inicio = linhas.length;
    while (inicio > 0 && /^\s*•\s+\S/.test(linhas[inicio - 1])) inicio--;

    const brutas = linhas.slice(inicio).map(linha => linha.replace(/^\s*•\s*/, '').trim());
    if (brutas.length < 2) return;

    // "Salmão batido — R$ 62,00" → título + preço p/ layout de linhas de menu
    const opcoes = brutas.map(opcao => {
      const partes = opcao.match(/^(.+?)\s*[—–]\s*((?:[^—–]*\bR\$\s?[\d.,]+)[^—–]*)$/);
      return partes ? { titulo: partes[1], preco: partes[2] } : { titulo: opcao, preco: '' };
    });

    turno.corpoExibicao = linhas.slice(0, inicio).join('\n').replace(/\s+$/, '');
    turno.opcoesLista = opcoes;
    // maioria com preço → vira menu (título esquerda, preço direita); senão, chips
    turno.opcoesComPreco = opcoes.filter(opcao => opcao.preco).length >= Math.ceil(opcoes.length / 2);
  }

  opcoesVisiveis(turno: any): Array<{ titulo: string; preco: string }> {
    const opcoes = turno.opcoesLista || [];
    if (opcoes.length <= MAXIMO_DE_OPCOES_SEM_COLAPSAR || turno.opcoesExpandidas) return opcoes;
    return opcoes.slice(0, OPCOES_VISIVEIS_COLAPSADO);
  }

  opcoesColapsaveis(turno: any): boolean {
    return (turno.opcoesLista || []).length > MAXIMO_DE_OPCOES_SEM_COLAPSAR;
  }

  alterneOpcoes(turno: any) {
    turno.opcoesExpandidas = !turno.opcoesExpandidas;
  }

  private labelDoDia(data: string | Date): string {
    const dia = new Date(data);
    const hoje = new Date();
    const ontem = new Date();
    ontem.setDate(hoje.getDate() - 1);

    if (dia.toDateString() === hoje.toDateString()) return 'Hoje';
    if (dia.toDateString() === ontem.toDateString()) return 'Ontem';
    return dia.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  horaRelativa(data: string | Date): string {
    const momento = new Date(data);
    const hoje = new Date();
    const ontem = new Date();
    ontem.setDate(hoje.getDate() - 1);

    if (momento.toDateString() === hoje.toDateString()) {
      return momento.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (momento.toDateString() === ontem.toDateString()) return 'Ontem';
    return momento.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  nomeExibicao(conversa: any): string {
    return conversa?.nomeContato || this.formateTelefone(conversa?.telefone);
  }

  telefoneDoContato(telefone: string): string {
    const digitos = String(telefone || '').replace(/\D/g, '');
    if (!digitos) return telefone;
    if (/^55\d{2}[6-9]\d{7}$/.test(digitos)) {
      return `+${digitos.slice(0, 4)}9${digitos.slice(4)}`;
    }
    return `+${digitos}`;
  }

  formateTelefone(telefone: string): string {
    if (!telefone) return '';
    const digitos = telefone.replace(/\D/g, '').replace(/^55/, '');
    if (digitos.length === 11) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
    if (digitos.length === 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
    return telefone;
  }

  iniciais(conversa: any): string {
    const nome = conversa?.nomeContato;
    if (nome) {
      const partes = nome.trim().split(/\s+/);
      return (partes[0][0] + (partes[1]?.[0] || '')).toUpperCase();
    }
    const digitos = (conversa?.telefone || '').replace(/\D/g, '');
    return digitos.slice(-2) || '?';
  }

  corAvatar(conversa: any): string {
    const chave = String(conversa?.telefone || conversa?.id || '');
    let hash = 0;
    for (let i = 0; i < chave.length; i++) hash = (hash * 31 + chave.charCodeAt(i)) >>> 0;
    return CORES_AVATAR[hash % CORES_AVATAR.length];
  }

  /** Chip verde da lista: a conversa fechou pedido (vínculo GERADO do atendimento). */
  rotuloVendas(conversa: any): string {
    const pedidos = Number(conversa?.pedidosGerados) || 0;
    return pedidos > 1 ? `${pedidos} vendas` : 'venda';
  }

  tituloVendas(conversa: any): string {
    const pedidos = Number(conversa?.pedidosGerados) || 0;
    return pedidos > 1
      ? `Esta conversa fechou ${pedidos} pedidos`
      : 'Esta conversa fechou um pedido';
  }

  previewMensagem(conversa: any): string {
    const prefixo = conversa.ultimoPapel === 'mia' ? '🤖 ' : (conversa.ultimoPapel === 'atendente' ? '🎧 ' : '');
    return prefixo + (conversa.ultimaMensagem || '');
  }

  linkWhatsapp(conversa: any): string {
    const digitos = (conversa?.telefone || '').replace(/\D/g, '');
    return `https://wa.me/${digitos}`;
  }

  /** Soma tokens da conversa e junta os modelos que apareceram nos turnos. */
  private calculeResumoTelemetria(turnos: any[]) {
    const modelos: string[] = [];
    const porModelo: { [modelo: string]: { turnos: number; custoUsd: number } } = {};
    let entrada = 0, saida = 0, cache = 0, cacheEscrita = 0;
    let turnosMia = 0, turnosSemIA = 0;

    for (const turno of turnos || []) {
      if (turno.modelo && modelos.indexOf(turno.modelo) === -1) modelos.push(turno.modelo);
      entrada += Number(turno.tokensEntrada) || 0;
      saida += Number(turno.tokensSaida) || 0;
      cache += Number(turno.tokensCache) || 0;
      cacheEscrita += Number(turno.tokensCacheEscrita) || 0;

      if (turno.papel === 'mia') {
        turnosMia++;
        if (turno.modelo === 'atalho-categoria') turnosSemIA++;
      }
      if (turno.modelo && turno.modelo !== 'atalho-categoria') {
        const acumulado = porModelo[turno.modelo] || (porModelo[turno.modelo] = { turnos: 0, custoUsd: 0 });
        acumulado.turnos++;
        acumulado.custoUsd += Number(turno.custoUsd) || 0;
      }
    }

    return modelos.length || entrada || saida
      ? { modelos, entrada, saida, cache, cacheEscrita, turnosMia, turnosSemIA, porModelo }
      : null;
  }

  /**
   * % dos tokens de entrada servidos pelo cache. Escrita conta como "não veio
   * do cache" — é justamente o token que pagou preço cheio (e mais 25%).
   */
  cacheHitDaConversa(): number | null {
    const resumo: any = this.resumoTelemetria;
    if (!resumo) return null;
    const total = resumo.entrada + resumo.cache + resumo.cacheEscrita;
    if (!total || !resumo.cache) return null;
    return Math.round((resumo.cache / total) * 100);
  }

  /** "3 de 12 sem IA" — quantos turnos da Mia saíram por atalho determinístico. */
  turnosSemIA(): string {
    const resumo: any = this.resumoTelemetria;
    if (!resumo?.turnosSemIA) return '';
    return `${resumo.turnosSemIA} de ${resumo.turnosMia} sem IA`;
  }

  /** Custo médio por turno de IA, com a quebra por modelo no tooltip. */
  custoMedioPorTurno(): string {
    const porModelo: any = (this.resumoTelemetria as any)?.porModelo || {};
    let turnos = 0, custo = 0;
    for (const modelo of Object.keys(porModelo)) {
      turnos += porModelo[modelo].turnos;
      custo += porModelo[modelo].custoUsd;
    }
    if (!turnos) return '';
    return `${this.custoEmReaisFormatado(custo / turnos)}/turno`;
  }

  tituloCustoMedio(): string {
    const porModelo: any = (this.resumoTelemetria as any)?.porModelo || {};
    const partes = Object.keys(porModelo).map(modelo => {
      const dados = porModelo[modelo];
      return `${this.rotuloModelo(modelo)}: ${this.custoEmReaisFormatado(dados.custoUsd / dados.turnos)}/turno em ${dados.turnos} turno(s)`;
    });
    return 'Custo médio por turno de IA · ' + partes.join(' · ');
  }

  /** Nome curto do modelo p/ caber no balão — o id completo fica no tooltip. */
  rotuloModelo(modelo: string): string {
    const id = String(modelo || '');
    if (!id) return '';
    if (id === 'atalho-categoria') return 'sem IA';
    return id.replace(/^claude-/, '').replace(/-\d{8}$/, '');
  }

  /** Modelos da conversa em rótulo curto (uma loja pode ter trocado no meio). */
  modelosDaConversa(): string {
    return (this.resumoTelemetria?.modelos || []).map(modelo => this.rotuloModelo(modelo)).join(' + ');
  }

  /**
   * "1593→279" (entrada→saída) do turno; vazio quando não houve chamada de IA.
   * Number exato aqui (como no simulador) — por turno cabe; o resumo do topo,
   * que soma a conversa toda, é que usa o formato compacto.
   */
  tokensDoTurno(turno: any): string {
    const entrada = Number(turno?.tokensEntrada) || 0;
    const saida = Number(turno?.tokensSaida) || 0;
    if (!entrada && !saida) return '';
    return `${entrada}→${saida}`;
  }

  /**
   * Cache do turno no badge (escrita + leitura somados) — sem ele o custo parece
   * errado: no 1º turno o system prompt inteiro é gravado no cache e domina o
   * valor, mas não aparece no "entrada→saída". O detalhe fica no tooltip.
   */
  cacheDoTurno(turno: any): string {
    const total = (Number(turno?.tokensCache) || 0) + (Number(turno?.tokensCacheEscrita) || 0);
    if (!total) return '';
    return `cache ${this.formateTokens(total)}`;
  }

  /** Milhares viram "32,4k" para o número não dominar a linha. */
  formateTokens(valor: number): string {
    const numero = Number(valor) || 0;
    if (numero < 1000) return String(numero);
    return `${(numero / 1000).toFixed(1).replace('.', ',')}k`;
  }

  custoEmReaisFormatado(valorUsd: number): string {
    return formateCustoEmReais(valorUsd);
  }

  tituloCustoEmReais(): string {
    return 'Custo estimado em reais · PTAX venda de 28/07/2026: US$ 1 = R$ 5,1177';
  }

  tituloTelemetria(turno: any): string {
    if (!turno?.modelo) return '';
    const partes = [
      turno.modelo,
      `${turno.tokensEntrada || 0} de entrada`,
      `${turno.tokensSaida || 0} de saída`,
    ];
    const cacheEscrita = Number(turno.tokensCacheEscrita) || 0;
    const cacheLeitura = Number(turno.tokensCache) || 0;
    if (cacheEscrita) partes.push(`${cacheEscrita} gravados no cache (1,25× o preço de entrada)`);
    if (cacheLeitura) partes.push(`${cacheLeitura} lidos do cache`);
    if (turno.custoUsd != null) partes.push(this.custoEmReaisFormatado(turno.custoUsd));
    if (turno.latenciaMs != null) partes.push(`${turno.latenciaMs}ms`);
    return partes.join(' · ') + ' · PTAX venda 28/07/2026';
  }

  /** Latência entra no badge só quando estourou o limite — o resto fica no detalhe. */
  latenciaLenta(turno: any): boolean {
    return (Number(turno?.latenciaMs) || 0) >= LATENCIA_LENTA_MS;
  }

  latenciaFormatada(ms: number): string {
    const valor = Number(ms) || 0;
    if (valor < 1000) return `${valor}ms`;
    return `${(valor / 1000).toFixed(1).replace('.', ',')}s`;
  }

  alterneDetalheCusto(turno: any) {
    // atalho não tem custo para quebrar; abrir o detalhe só confundiria
    if (turno?.papel !== 'mia' || !turno?.modelo || turno.modelo === 'atalho-categoria') return;
    turno.detalheCustoAberto = !turno.detalheCustoAberto;
  }

  /** Snapshot de preços gravado com o turno; null em turnos antigos (sem quebra confiável). */
  private precosDoTurno(turno: any): { entrada: number; saida: number; cache: number; cacheEscrita: number } | null {
    if (!turno?.precosUsdMtok) return null;
    try {
      const precos = JSON.parse(turno.precosUsdMtok);
      return typeof precos?.entrada === 'number' ? precos : null;
    } catch {
      return null;
    }
  }

  /**
   * Quebra do custo do turno nos 4 componentes, cada um com tokens × preço do
   * snapshot — por isso a soma sempre fecha com o custoUsd gravado, mesmo que
   * a tabela de preços de hoje seja outra.
   */
  parcelasDoTurno(turno: any): Array<{ rotulo: string; tokens: number; precoMtok: number; usd: number; pct: number }> | null {
    const precos = this.precosDoTurno(turno);
    if (!precos) return null;

    const linhas = [
      { rotulo: 'Entrada (sem cache)', tokens: Number(turno.tokensEntrada) || 0, precoMtok: precos.entrada },
      { rotulo: 'Gravação de cache', tokens: Number(turno.tokensCacheEscrita) || 0, precoMtok: precos.cacheEscrita },
      { rotulo: 'Leitura de cache', tokens: Number(turno.tokensCache) || 0, precoMtok: precos.cache },
      { rotulo: 'Saída', tokens: Number(turno.tokensSaida) || 0, precoMtok: precos.saida },
    ].filter(linha => linha.tokens > 0);

    const total = linhas.reduce((soma, linha) => soma + linha.tokens * linha.precoMtok, 0) / 1_000_000;
    return linhas.map(linha => {
      const usd = (linha.tokens * linha.precoMtok) / 1_000_000;
      return { ...linha, usd, pct: total ? Math.round((usd / total) * 100) : 0 };
    });
  }

  /** Contrafactual: todos os tokens de entrada a preço cheio, sem cache algum. */
  custoSemCacheUsd(turno: any): number | null {
    const precos = this.precosDoTurno(turno);
    if (!precos) return null;
    const entradaTotal = (Number(turno.tokensEntrada) || 0) + (Number(turno.tokensCache) || 0) +
      (Number(turno.tokensCacheEscrita) || 0);
    if (!entradaTotal) return null;
    return (entradaTotal * precos.entrada + (Number(turno.tokensSaida) || 0) * precos.saida) / 1_000_000;
  }

  formateUsd(valor: number): string {
    const numero = Number(valor) || 0;
    return 'US$ ' + numero.toFixed(numero >= 0.01 ? 4 : 5).replace('.', ',');
  }

  formatePrecoMtok(valor: number): string {
    return '$' + (Number(valor) || 0).toFixed(2).replace('.', ',') + '/MTok';
  }

  tituloStatusWhatsapp(status: string): string {
    switch (status) {
      case 'sent': return 'Enviada';
      case 'delivered': return 'Entregue';
      case 'read': return 'Visualizada';
      case 'failed': return 'Falha no envio';
      default: return '';
    }
  }
}
